// Billing / entitlements / usage API client (#168 WS2). Same auth pattern as
// iamApi; the interceptor below also rewrites the structured 402/403 billing
// envelopes into `detail` so every existing error surface renders them.
import axios from 'axios';
import { resolveApiBaseUrl } from './resolveApiBaseUrl';
import { attachAuth } from './authToken';

const base = resolveApiBaseUrl(); // ends with /api
const client = axios.create({ baseURL: `${base}/billing` });
client.interceptors.request.use(attachAuth);

// App-wide envelope rendering: components across the app show
// e?.response?.data?.detail — map the billing envelopes onto it so a
// limit_reached/feature_not_in_plan from ANY endpoint reads as an actionable
// upgrade message without per-component changes.
export function installBillingEnvelopeInterceptor(axiosInstance) {
  axiosInstance.interceptors.response.use(undefined, (error) => {
    const d = error?.response?.data;
    if (d && (d.error === 'limit_reached' || d.error === 'feature_not_in_plan') && !d.detail) {
      d.detail = `${d.message} Upgrade your plan to continue.`;
      d.billing = true;
    }
    return Promise.reject(error);
  });
}
installBillingEnvelopeInterceptor(client);

const d = (p) => p.then((r) => r.data);

export const billingApi = {
  plans: () => d(client.get('/plans')),
  subscription: () => d(client.get('/subscription')),
  entitlements: () => d(client.get('/entitlements')),
  usage: (scope = 'me', period, projectId) =>
    d(client.get('/usage', { params: { scope, period, project_id: projectId } })),
  checkout: (planKey, cycle = 'monthly') =>
    d(client.post('/checkout', { plan_key: planKey, cycle })),

  // super-admin console
  adminOverview: (period) => d(client.get('/admin/overview', { params: { period } })),
  adminSetSubscription: (oid, planKey) =>
    d(client.put(`/admin/orgs/${oid}/subscription`, { plan_key: planKey })),
  adminSetOverride: (oid, key, body) => d(client.put(`/admin/orgs/${oid}/overrides/${key}`, body)),
  adminDelOverride: (oid, key) => d(client.delete(`/admin/orgs/${oid}/overrides/${key}`)),
  adminSetUserOverride: (oid, uid, key, body) =>
    d(client.put(`/admin/orgs/${oid}/users/${uid}/overrides/${key}`, body)),
  // #563: billing.jsx has always called this for the user-level override Remove
  // button, but the method did not exist here — the button threw. The org-level
  // twin (adminDelOverride) worked, which is why it went unnoticed.
  adminDelUserOverride: (oid, uid, key) =>
    d(client.delete(`/admin/orgs/${oid}/users/${uid}/overrides/${key}`)),
  adminResetUsage: (oid, body) => d(client.post(`/admin/orgs/${oid}/usage/reset`, body)),

  // Plan editing (#563). Deliberately NOT built on `plans()`: that is the PUBLIC
  // catalog, which filters `is_public IS TRUE` and flattens each entitlement to a
  // single value. Editing on top of it would (a) hide every non-public plan from the
  // only screen able to un-hide one — so a mis-click on "Public" strands a plan with
  // no way back through the product — and (b) drop `enforcement`, which the
  // entitlement editor has to round-trip.
  adminPlans: () => d(client.get('/admin/plans')),
  adminCreatePlan: (body) => d(client.post('/admin/plans', body)),
  adminUpdatePlan: (pid, body) => d(client.put(`/admin/plans/${pid}`, body)),
  adminSetPlanEntitlement: (pid, key, body) =>
    d(client.put(`/admin/plans/${pid}/entitlements/${key}`, body)),
};

export default billingApi;
